import {
  AmcProtectedPatientDTO,
  UserPatientProfile,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { Button } from '@mui/material';
import { styled, css } from '@mui/material/styles';
import dayjs from 'dayjs';
import {
  DEFAULT_TIMEZONE,
  POLICIES_ORGANISATIONS_COORDONNEES_TERRITORIALES,
} from '@/src/constants';
import {
  InstructionSection,
  TLC_INSTRUCTION,
} from '@/components/utils/instruction';
import { RejectionDialogStatus } from './Steps/SelectPatient/RejectionDialog';

// TLC AMC Instructions data
export const amcTlcInstructionSections: InstructionSection[] = [
  {
    sectionTitle: 'tlc-instruction_plural',
    instructionItems: [
      TLC_INSTRUCTION,
      {
        id: 'life_threatening_emergency',
        instruction: 'life_threatening_emergency',
        shouldTranslate: true,
      },
      {
        id: 'go_doctor_first',
        instruction: 'go_doctor_first',
        shouldTranslate: true,
      },
      {
        id: 'checked_availability_of_practitioners',
        instruction: 'checked_availability_of_practitioners',
        url: `${POLICIES_ORGANISATIONS_COORDONNEES_TERRITORIALES}`,
        shouldTranslate: true,
      },
    ],
  },
  {
    sectionTitle: 'not_suitable_service',
    instructionItems: [
      {
        id: 'controlled_drugs',
        instruction: 'controlled_drugs',
        shouldTranslate: true,
      },
      {
        id: 'localized_pain',
        instruction: 'localized_pain',
        shouldTranslate: true,
      },
      {
        id: 'long_term_illness',
        instruction: 'long_term_illness',
        shouldTranslate: true,
      },
    ],
  },
];

export const StyledButton = styled(Button)(
  ({ theme }) => css`
    width: 100%;
    height: ${theme.spacing(7)};
    border-radius: ${theme.spacing(1)};
    border-width: 1px;
    justify-content: space-between;
    color: ${theme.palette.background.darkGrey};
    font-size: ${theme.typography.body1.fontSize};
    padding: ${theme.spacing(0.5, 2)};
  `,
);

// TLC AMC types (to be moved to data-model-v2 on feature completion)
// Steps names
export const INTRODUCTION = 'INTRODUCTION';
export const MAINTENANCE = 'MAINTENANCE';
export const SELECT_PATIENT = 'SELECT_PATIENT';
export const SELECT_SPECIALITY = 'SELECT_SPECIALITY';
export const SELECT_CONSULTATION_REASON = 'SELECT_CONSULTATION_REASON';
export const SELECT_SYMPTOMS = 'SELECT_SYMPTOMS';
export const UPDATE_MEDICAL_PROFILE = 'UPDATE_MEDICAL_PROFILE';
export const UPDATE_HEALTH_ID_AND_AMC = 'UPDATE_HEALTH_ID_AND_AMC';
export const SUMMARY = 'SUMMARY';

// TODO label translation
export const CONSULTATION_REASONS = [
  {
    label: 'Conseils ou recommandations',
    documentRequired: false,
    id: 'ADVICE_INFORMATION',
  },
  {
    label: 'Interprétation de résultats',
    documentRequired: true,
    id: 'RESULT_INTERPRETATION',
  },
  {
    label: 'Problème médical',
    documentRequired: false,
    id: 'MEDICAL_PROBLEM',
  },
  {
    label: "Renouvellement d'ordonnance",
    documentRequired: true,
    id: 'PRESCRIPTION_RENEWAL',
  },
  {
    label: 'Demande liée à un document',
    documentRequired: false,
    id: 'DOCUMENT_RELATED',
  },
];

export const getConsultationReasonCategory = (
  consultationReasonLabel: string | null,
) => {
  const selectedReason = CONSULTATION_REASONS.find(
    reason => consultationReasonLabel === reason.label,
  );
  return selectedReason && selectedReason.id;
};

export type DialogSteps = null | 'initial' | 'upload' | 'choose';

export type ErrorObject = {
  code: string;
  subCodes: string[];
  message: string;
};

export type EndSharingType = (
  result?: 'fail' | 'success',
  error?: ErrorObject,
) => void;

export type TlcAmcState = {
  activeStep:
    | typeof INTRODUCTION
    | typeof MAINTENANCE
    | typeof SELECT_PATIENT
    | typeof SELECT_SPECIALITY
    | typeof SELECT_CONSULTATION_REASON
    | typeof SELECT_SYMPTOMS
    | typeof UPDATE_MEDICAL_PROFILE
    | typeof UPDATE_HEALTH_ID_AND_AMC
    | typeof SUMMARY;
  profileId: string | null;
  specialityId: string | null;
  consultationReasonLabel: string | null;
  mutualBrandExternalId: string | null;
  teleconsultationRelayId: string | null;
  amcTlcType: 'TLC_24_7' | 'TLC_8_TO_20' | null;
  documentToShareIds: string[];
  linkType: 'SSO' | 'IDB' | 'TLCA' | 'SSO_AUTOLOGIN' | null;
  symptoms: string[];
};

export type ProtectedPatient = AmcProtectedPatientDTO;

export type TlcAmcSpeciality = {
  id: string;
  name: string;
  shortName: string;
};

const FLAG_REJECT_CHILD_ENABLED = true;
const TLC_AMC_MINIMUM_AGE = 3;

// ==============================================================================
// Helper functions
// ==============================================================================

export const getPrimaryProtectedPatient = (
  protectedPatients,
  defaultPatient,
) => {
  // apply priority criteria on protected patients to choose which protected patient on the AMC card and AMC landing page
  // -if there is one protected patient return obj
  // -filter the non-zero remaining quota | null
  // -filter 24/7 tlc offer
  // -filter by main patient
  // -filter with null quota and return obj if not filter max quota and return obj

  if (protectedPatients.length === 1) return protectedPatients[0];

  const applyFilterWithFallback = (patients, filterFn) => {
    const filtered = patients.filter(filterFn);
    return filtered.length > 0 ? filtered : patients;
  };

  const byActiveLink = protectedPatient =>
    protectedPatient.linkType === 'IDB' ||
    (protectedPatient.linkType === 'SSO' &&
      dayjs().isBefore(dayjs(protectedPatient.expirationDate)));

  const byQuota = protectedPatient =>
    protectedPatient.service.remainingQuota === null ||
    protectedPatient.service.remainingQuota > 0;

  const byOffer247 = protectedPatient =>
    protectedPatient.service.offer.includes('24');

  const byMainPatient = protectedPatient =>
    protectedPatient.patientProfileId === defaultPatient.id;

  const filteredProtectedPatientsWithActiveLink = applyFilterWithFallback(
    protectedPatients,
    byActiveLink,
  );

  const filteredProtectedPatientsWithQuota = applyFilterWithFallback(
    filteredProtectedPatientsWithActiveLink,
    byQuota,
  );

  const filteredProtectedPatientsWithTlcOffer24 = applyFilterWithFallback(
    filteredProtectedPatientsWithQuota,
    byOffer247,
  );

  const filteredProtectedPatientsWithMainPatient = applyFilterWithFallback(
    filteredProtectedPatientsWithTlcOffer24,
    byMainPatient,
  );

  // free quota priority vs. non-zero,
  const filterProtectedPatientsWithMainPatientWithFreeQuota = filteredProtectedPatientsWithMainPatient.filter(
    patient => patient.service.remainingQuota === null,
  );
  if (filterProtectedPatientsWithMainPatientWithFreeQuota.length) {
    return filterProtectedPatientsWithMainPatientWithFreeQuota[0];
  }

  //  get an obj with max quota ,
  const filterProtectedPatientsWithMainPatientWithBiggerQuota = filteredProtectedPatientsWithMainPatient.reduce(
    (prev, current) => {
      return Number(prev.service.remainingQuota) >
        Number(current.service.remainingQuota)
        ? prev
        : current;
    },
  );
  return filterProtectedPatientsWithMainPatientWithBiggerQuota;
};

// /////////

const startTime = dayjs('08:00', 'HH:mm');
const endTime = dayjs('20:00', 'HH:mm');

export type CuratedProfile = Pick<
  UserPatientProfile,
  'id' | 'firstName' | 'lastName' | 'birthDate'
> & {
  rejectionReason?: RejectionDialogStatus;
  remainingQuota?: number | null;
  amcTlcType?: 'TLC_24_7' | 'TLC_8_TO_20';
  mutualBrandExternalId?: string | null;
  teleconsultationRelayId?: string | null;
  linkType?: 'SSO' | 'IDB' | 'TLCA' | 'SSO_AUTOLOGIN';
};

export const getMergedProfilesWithRemainingQuota = (
  protectedPatients: ProtectedPatient[],
  allProfiles: UserPatientProfile[],
) => {
  const allProfilesCurated = allProfiles.map(profile => {
    const { id, firstName, lastName, birthDate } = profile;

    const curatedProfile: CuratedProfile = {
      id,
      firstName,
      lastName,
      birthDate,
    };

    // Normally, a profile will be covered by a single contract,
    // but we check in case covered by more (edge case)
    const profileCoverage = protectedPatients.filter(
      protectedProfile => profile.id === protectedProfile.patientProfileId,
    );

    if (profileCoverage.length === 0) {
      curatedProfile.rejectionReason = 'notProtectedPatient';
      return curatedProfile;
    }

    if (
      FLAG_REJECT_CHILD_ENABLED &&
      dayjs().diff(birthDate, 'year') < TLC_AMC_MINIMUM_AGE
    ) {
      curatedProfile.rejectionReason = 'tooYoung';
      return curatedProfile;
    }

    const {
      service = {},
      partnerId,
      expirationDate,
      linkType,
      teleconsultationRelayId,
    } = profileCoverage[0];

    if (profileCoverage.length === 1) {
      curatedProfile.remainingQuota = service.remainingQuota;
      curatedProfile.amcTlcType = service.offer;
      curatedProfile.mutualBrandExternalId = partnerId;
      curatedProfile.teleconsultationRelayId = teleconsultationRelayId;
      curatedProfile.linkType = linkType;

      // No remaining quota
      if (service.remainingQuota === 0 && service.remainingQuota !== null) {
        curatedProfile.rejectionReason = 'zeroQuota';
        return curatedProfile;
      }

      // SSO link expired
      if (dayjs().isAfter(dayjs(expirationDate))) {
        curatedProfile.rejectionReason =
          linkType === 'SSO' ? 'linkExpired' : 'linkMutualExpired';
        return curatedProfile;
      }

      // Out of offer time (> 8am-8pm <)
      if (
        service.offer === 'TLC_8_TO_20' &&
        !dayjs()
          .tz(DEFAULT_TIMEZONE)
          .isBetween(startTime, endTime)
      ) {
        curatedProfile.rejectionReason = 'invalidTime';
        return curatedProfile;
      }
    } else if (profileCoverage.length > 1) {
      // Handle multiple contracts

      // sumRemainingQuota
      // the same patient has multiple AMCs
      let expiredNullQota;
      curatedProfile.remainingQuota = profileCoverage.reduce(
        (previousQuota, currentQuota, index) => {
          if (index > 0 && previousQuota === null) {
            return null;
          }
          if (currentQuota?.service?.remainingQuota === null) {
            if (
              dayjs().isBefore(dayjs(currentQuota?.expirationDate)) ||
              currentQuota.linkType === 'IDB'
            ) {
              return null;
            }
            if (dayjs().isAfter(dayjs(currentQuota?.expirationDate))) {
              expiredNullQota = currentQuota;
            }
          }
          // if total is 0 and has freeQuota but expired
          if (
            expiredNullQota &&
            index === profileCoverage.length - 1 &&
            previousQuota === 0
          ) {
            return null;
          }

          // sum curr and prev quota
          if (currentQuota?.service?.remainingQuota) {
            return Number(previousQuota) + currentQuota.service.remainingQuota;
          }

          return Number(previousQuota) + 0;
        },
        null as number | null,
      );

      // SSO link expired
      const validAmcPatient = profileCoverage.filter(
        patient =>
          patient.linkType === 'IDB' ||
          dayjs().isBefore(dayjs(patient.expirationDate)),
      );

      if (!validAmcPatient.length) {
        curatedProfile.rejectionReason = 'linkExpired';
        return curatedProfile;
      }

      // free quota || non-zero,
      const patientWithRemainingQuota = validAmcPatient?.filter(
        patient =>
          patient?.service?.remainingQuota === null ||
          (patient?.service?.remainingQuota ?? 0) > 0,
      );

      if (!patientWithRemainingQuota?.length) {
        curatedProfile.rejectionReason = 'zeroQuota';
        return curatedProfile;
      }

      let protectedLinksInRightHourInterval = patientWithRemainingQuota;
      // we are not in time (> 8am-8pm <) interval we are keeping only 24_7 protected links
      if (
        !dayjs()
          .tz(DEFAULT_TIMEZONE)
          .isBetween(startTime, endTime)
      ) {
        protectedLinksInRightHourInterval = patientWithRemainingQuota?.filter(
          link => link?.service?.offer === 'TLC_24_7',
        );
      }
      // we have some protected link but no 24_7 so we are out of the (> 8am-8pm <) interval
      if (!protectedLinksInRightHourInterval?.length) {
        curatedProfile.rejectionReason = 'invalidTime';
        return curatedProfile;
      }

      // 24/7 priority vs. 8 am-8 pm
      const filterProtectedPatientsWithTlcOffer24 = protectedLinksInRightHourInterval.filter(
        protectedPatient => protectedPatient?.service?.offer?.includes('24'),
      );

      const filterProtectedPatientsByTlcOffer = filterProtectedPatientsWithTlcOffer24.length
        ? filterProtectedPatientsWithTlcOffer24
        : protectedLinksInRightHourInterval;

      // The system will deduct from the AMC protection with a higher quota
      //  max quota ,
      const patientWithBiggerQuota = filterProtectedPatientsByTlcOffer.reduce(
        (prev, current) => {
          return Number(prev?.service?.remainingQuota ?? 0) >
            Number(current?.service?.remainingQuota ?? 0)
            ? prev
            : current;
        },
      );

      curatedProfile.amcTlcType = patientWithBiggerQuota.service?.offer;
      curatedProfile.mutualBrandExternalId = patientWithBiggerQuota.partnerId;
      curatedProfile.teleconsultationRelayId =
        patientWithBiggerQuota.teleconsultationRelayId;
      curatedProfile.linkType = patientWithBiggerQuota.linkType;
    }
    return curatedProfile;
  });

  return allProfilesCurated;
};
